import { LayaGL } from "../../layagl/LayaGL";
import { Color } from "../../maths/Color";
import { Vector2 } from "../../maths/Vector2";
import { Vector4 } from "../../maths/Vector4";
import { BaseRenderNode2D } from "../../NodeRender2D/BaseRenderNode2D";
import { RenderState } from "../../RenderDriver/RenderModuleData/Design/RenderState";
import { IndexFormat } from "../../RenderEngine/RenderEnum/IndexFormat";
import { Shader3D, ShaderFeatureType } from "../../RenderEngine/RenderShader/Shader3D";
import { Material } from "../../resource/Material";
import { Mesh2D, VertexMesh2D } from "../../resource/Mesh2D";
import { Texture } from "../../resource/Texture";
import { ShaderDefines2D } from "../../webgl/shader/d2/ShaderDefines2D";
export class Mesh2DRender extends BaseRenderNode2D {
    static __init__() {
        if (Mesh2DRender.mesh2DDefaultMaterial)
            return;
        Mesh2DRender.mesh2DDefaultMaterial = new Material();
        Mesh2DRender.mesh2DDefaultMaterial.setShaderName("baseRender2D");
        Mesh2DRender.mesh2DDefaultMaterial.setBoolByIndex(Shader3D.DEPTH_WRITE, false);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.DEPTH_TEST, RenderState.DEPTHTEST_OFF);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.BLEND, RenderState.BLEND_ENABLE_ALL);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.BLEND_EQUATION, RenderState.BLENDEQUATION_ADD);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.BLEND_SRC, RenderState.BLENDPARAM_ONE);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.BLEND_DST, RenderState.BLENDPARAM_ONE_MINUS_SRC_ALPHA);
        Mesh2DRender.mesh2DDefaultMaterial.setFloatByIndex(ShaderDefines2D.UNIFORM_VERTALPHA, 1.0);
        Mesh2DRender.mesh2DDefaultMaterial.setIntByIndex(Shader3D.CULL, RenderState.CULL_NONE);
        Mesh2DRender.mesh2DDefaultMaterial.lock = true;
        let vbDecl = VertexMesh2D.getVertexDeclaration(["POSITION,UV"]);
        let vbData = new Float32Array([
            0, 0, 0, 0, 0,
            1, 0, 0, 1, 0,
            1, 1, 0, 1, 1,
            0, 1, 0, 0, 1
        ]);
        let ibData = new Uint16Array([0, 1, 2, 0, 2, 3]);
        Mesh2DRender.defaultQuadMesh = Mesh2D.createMesh2DByPrimitive([vbData], vbDecl, ibData, IndexFormat.UInt16, [{ start: 0, length: 6 }]);
    }
    _createRenderHandle() {
        return LayaGL.render2DRenderPassFactory.createMesh2DRenderDataHandle();
    }
    _initDefaultRenderData() {
        this.color = new Color();
        this.tilingOffset = new Vector4(0, 0, 1, 1);
        this.texture = null;
        this._applyUnitQuad(this._useUnitQuad);
    }
    _isMaterialVaild(value) {
        return value.checkType(ShaderFeatureType.D2_BaseRenderNode2D);
    }
    renderUpdate(context) {
        this._updateLight();
    }
    set useUnitQuad(value) {
        if (this._useUnitQuad === value)
            return;
        this._applyUnitQuad(value);
    }
    get useUnitQuad() {
        return this._useUnitQuad;
    }
    set sharedMesh(value) {
        if (this._sharedMesh == value)
            return;
        if (this._sharedMesh) {
            this._sharedMesh._removeReference();
        }
        if (value) {
            if (!value._vertexBuffers) {
                value = null;
                console.warn("not a 2D mesh");
            }
            else {
                value._addReference();
            }
        }
        this._sharedMesh = value;
        this._applyUnitQuad(!value);
    }
    get sharedMesh() {
        return this._sharedMesh;
    }
    set color(value) {
        this._renderHandle.baseColor = value;
        this._notifyDataChange();
    }
    get color() {
        return this._renderHandle.baseColor;
    }
    set size(value) {
        value.cloneTo(this._size);
        this._spriteShaderData.setVector2(BaseRenderNode2D.BASERENDERSIZE, this._size);
        this._notifyDataChange();
    }
    get size() { return this._size; }
    set tilingOffset(value) {
        this._tilingOffset = value;
        this._updateTilingOffset();
        this._notifyDataChange();
    }
    get tilingOffset() {
        return this._tilingOffset;
    }
    _updateTilingOffset() {
        let tilingOffset = this._renderHandle.tilingOffset;
        if (this._tilingOffset == null) {
            tilingOffset.setValue(this._textureTilingOffset.x, this._textureTilingOffset.y, this._textureTilingOffset.z, this._textureTilingOffset.w);
        }
        else {
            tilingOffset.setValue(this._tilingOffset.x * this._textureTilingOffset.z + this._textureTilingOffset.x, this._tilingOffset.y * this._textureTilingOffset.w + this._textureTilingOffset.y, this._tilingOffset.z * this._textureTilingOffset.z, this._tilingOffset.w * this._textureTilingOffset.w);
        }
        this._renderHandle.tilingOffset = tilingOffset;
    }
    set texture(value) {
        if (this._texture instanceof Texture) {
            this._texture._removeReference();
        }
        this._texture = value;
        if (value instanceof Texture) {
            value._addReference();
            if (value.uv !== Texture.DEF_UV) {
                console.warn("Texture uv is not default, it will affect the tiling offset.");
                let sx = value.uvrect[2] / value.width;
                let sy = value.uvrect[3] / value.height;
                this._textureTilingOffset.setValue(value.uvrect[0] - value.offsetX * sx, value.uvrect[1] - value.offsetY * sy, value.sourceWidth * sx, value.sourceHeight * sy);
            }
            else {
                this._textureTilingOffset.setValue(0, 0, 1, 1);
            }
            value = value.bitmap;
        }
        else {
            this._textureTilingOffset.setValue(0, 0, 1, 1);
        }
        this._updateTilingOffset();
        this._renderHandle.baseTexture = value;
        this._notifyDataChange();
    }
    get texture() {
        return this._texture;
    }
    set normalTexture(value) {
        this._renderHandle.normal2DTexture = value;
        this._notifyDataChange();
    }
    get normalTexture() {
        return this._renderHandle.normal2DTexture;
    }
    set normalStrength(value) {
        this._renderHandle.normal2DStrength = value;
        this._notifyDataChange();
    }
    get normalStrength() {
        return this._renderHandle.normal2DStrength;
    }
    set sharedMaterial(value) {
        super.sharedMaterial = value;
        this._changeMesh();
        this._notifyDataChange();
    }
    get sharedMaterial() {
        return this._materials[0];
    }
    _getElementMaterial(index) {
        return this._materials[index] || Mesh2DRender.mesh2DDefaultMaterial;
    }
    _applyUnitQuad(value) {
        this._useUnitQuad = value;
        if (value) {
            this._spriteShaderData.addDefine(BaseRenderNode2D.SHADERDEFINE_UNITQUAD);
        }
        else {
            this._spriteShaderData.removeDefine(BaseRenderNode2D.SHADERDEFINE_UNITQUAD);
        }
        this._changeMesh();
        this._notifyDataChange();
    }
    _changeMesh() {
        let mesh = this._useUnitQuad ? Mesh2DRender.defaultQuadMesh : this._sharedMesh;
        let meshArrayDefine = new Array();
        if (this._activeMesh) {
            VertexMesh2D.getMeshDefine(this._activeMesh, meshArrayDefine);
            for (var i = 0, n = meshArrayDefine.length; i < n; i++)
                this._spriteShaderData.removeDefine(meshArrayDefine[i]);
            meshArrayDefine.length = 0;
        }
        if (mesh) {
            VertexMesh2D.getMeshDefine(mesh, meshArrayDefine);
            for (var i = 0, n = meshArrayDefine.length; i < n; i++)
                this._spriteShaderData.addDefine(meshArrayDefine[i]);
        }
        this._activeMesh = mesh;
        let submeshNum = mesh ? mesh.subMeshCount : 0;
        if (submeshNum < this._renderElements.length) {
            for (var i = this._renderElements.length, n = submeshNum; n < i; i--) {
                let element = this._renderElements[i - 1];
                BaseRenderNode2D._removeRenderElement2DMaterial(element, this._getElementMaterial(i - 1));
                element.destroy();
            }
            this._renderElements.length = submeshNum;
        }
        for (var i = 0, n = submeshNum; i < n; i++) {
            let element = this._renderElements[i];
            if (!element)
                element = this._renderElements[i] = LayaGL.render2DRenderPassFactory.createRenderElement2D();
            element.geometry = mesh.getSubMesh(i);
            element.value2DShaderData = this._spriteShaderData;
            BaseRenderNode2D._setRenderElement2DMaterial(element, this._getElementMaterial(i));
            element.renderStateIsBySprite = false;
            element.nodeCommonMap = this._getcommonUniformMap();
            element.owner = this._struct;
        }
        this._struct.renderElements = this._renderElements;
    }
    constructor() {
        super();
        this._useUnitQuad = false;
        this._textureTilingOffset = new Vector4(0, 0, 1, 1);
        this._size = new Vector2(1, 1);
        this._renderElements = [];
        this._materials = [];
    }
}
